export class DecimalFormatError extends Error {
  readonly code = 'domain.decimal_format'
  readonly value: string
  readonly expected: string
  constructor(value: string, expected: string) {
    super(`"${value}" is not a decimal matching ${expected}`)
    this.name = 'DecimalFormatError'
    this.value = value
    this.expected = expected
  }
}

/** Parses a fixed-scale decimal string into scaled integer units. */
export function parseScaled(value: string, scale: number, pattern: RegExp): bigint {
  if (!pattern.test(value)) throw new DecimalFormatError(value, String(pattern))
  const negative = value.startsWith('-')
  const unsigned = negative ? value.slice(1) : value
  const dot = unsigned.indexOf('.')
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot)
  const fraction = dot === -1 ? '' : unsigned.slice(dot + 1)
  const units = BigInt(whole + fraction.padEnd(scale, '0'))
  return negative ? -units : units
}

/** Renders scaled integer units as a plain decimal string with exactly `scale` fraction digits. */
export function formatScaled(units: bigint, scale: number): string {
  const negative = units < 0n
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0')
  const whole = digits.slice(0, digits.length - scale)
  const fraction = scale === 0 ? '' : `.${digits.slice(digits.length - scale)}`
  return `${negative ? '-' : ''}${whole}${fraction}`
}

/** Rescales units from `from` digits to `to` digits, rounding half away from zero. */
export function rescale(units: bigint, from: number, to: number): bigint {
  if (to >= from) return units * 10n ** BigInt(to - from)
  const divisor = 10n ** BigInt(from - to)
  const negative = units < 0n
  const magnitude = negative ? -units : units
  const quotient = magnitude / divisor
  const remainder = magnitude % divisor
  const rounded = remainder * 2n >= divisor ? quotient + 1n : quotient
  return negative ? -rounded : rounded
}
